import { PublishedTmsEventSchema } from "@storyboard/tms-event-contracts/published";
import type { TmsEvent } from "@storyboard/web-client";

/** The identifying triple of a row the client could not parse, for the reliability event that reports it. */
export type RejectedTmsEvent = { kind: string | null; version: number | null };

export type ParsedTmsEvents = { events: TmsEvent[]; rejected: RejectedTmsEvent[] };

/**
 * Accepts one wire row as a `TmsEvent` when it parses against the catalog's own DTO schema, else null.
 *
 * The schema is the contracts' `PublishedTmsEventSchema`, the very union the API declares its page with, so "this build has an
 * arm for it" is the catalog this build was generated from rather than a table kept beside the generated types.
 * Strict, as every contracts schema is: a kind the backend keeps internal, a version, or a field the catalog does not
 * name is a rejection here
 * and a report at the caller, never a row half-read. Nothing is converted between versions.
 */
export function parseTmsEvent(raw: unknown): TmsEvent | null {
  const parsed = PublishedTmsEventSchema.safeParse(raw);
  // The generated client's `TmsEvent` is the OpenAPI rendering of this same schema; the cast joins the two
  // declarations of one shape.
  return parsed.success ? (parsed.data as TmsEvent) : null;
}

/** Parses a page event by event, so one row this build cannot read never fails the rows beside it. */
export function parseTmsEvents(rows: readonly unknown[]): ParsedTmsEvents {
  const parsed: ParsedTmsEvents = { events: [], rejected: [] };
  for (const raw of rows) {
    const event = parseTmsEvent(raw);
    if (event) {
      parsed.events.push(event);
      continue;
    }
    const row = typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
    parsed.rejected.push({
      kind: typeof row["kind"] === "string" ? row["kind"] : null,
      version: typeof row["version"] === "number" ? row["version"] : null,
    });
  }
  return parsed;
}
